import React, { useState, useEffect } from 'react';
import '../styles/Design.css';
import { Link } from 'react-router-dom';
import { Sun, Moon } from 'lucide-react';
import '../styles/HomePage.css';

// Import your images
import icdcImage from '../assets/images/graphic design/icdc.png';
import figuresImage from '../assets/images/graphic design/figures.png';
// import dymunImage from '../assets/images/graphic design/mtac.png';
// import statementImage from '../assets/images/graphic design/ontario.png'; 

// Digital art
import getThatBread from '../assets/images/digital/get_that_bread.png';
import growth from '../assets/images/digital/growth.png';
import tops from '../assets/images/digital/tops.png';

// Paintings
import adam from '../assets/images/paintings/adam.jpg';
import big from '../assets/images/paintings/big.png';
import ladywithhat from '../assets/images/paintings/ladywithhat.JPG';
import george from '../assets/images/mixed_media/george.png';
import suga from '../assets/images/mixed_media/suga.JPG';
import tiger from '../assets/images/mixed_media/tiger.png';

// Graphic design images
import bannerImage from '../assets/images/graphic design/banner.png';
import bingoImage from '../assets/images/graphic design/bingo.png';
import chapterImage from '../assets/images/graphic design/chapter.png';
import mangoImage from '../assets/images/graphic design/mango.png';
import medImage from '../assets/images/graphic design/med.png';
import ontarioImage from '../assets/images/graphic design/ontario.png';
import prizeImage from '../assets/images/graphic design/prize.png';
import raffleImage from '../assets/images/graphic design/raffle.png';
import reachImage from '../assets/images/graphic design/reach.png';
import rebuildImage from '../assets/images/graphic design/rebuild.png';
import sleepImage from '../assets/images/graphic design/sleep.png';

// Preload images for better caching
const preloadImage = (src) => {
  const img = new Image();
  img.src = src;
};

// Static data - moved outside component to avoid dependency issues
const art = {
  'Digital Art': [
    {
      image: getThatBread,
    },
    {
      image: growth,
    },
    {
      image: tops,
    },
  ],
  Paintings: [
    {
      image: adam,
    },
    {
      image: big,
    },
    {
      image: ladywithhat,
    },
  ],
  Sketches: [
    {
      image: george,
    },
    {
      image: suga,
    },
    {
      image: tiger,
    },
  ],
};

const graphicDesignImages = [
  bannerImage,
  bingoImage,
  chapterImage,
  icdcImage,
  figuresImage,
  mangoImage,
  medImage,
  ontarioImage,
  prizeImage,
  raffleImage,
  reachImage,
  rebuildImage,
  sleepImage,
];

const Design = () => {
  const [activeArtTab, setActiveArtTab] = useState('Digital Art');
  const [selectedGraphicIndex, setSelectedGraphicIndex] = useState(null);
  const [isDark, setIsDark] = useState(() => {
    // On mobile, default to light mode
    const isMobile = window.innerWidth <= 640;
    if (isMobile) {
      return false;
    }
    // Read from localStorage on initial load for desktop
    const saved = localStorage.getItem('darkMode');
    return saved === 'true';
  });

  // const projects = {
  //   Branding: [
  //     {
  //       title: 'DECA ICDC 2023 Apparel',
  //       description: 't-shirt design featuring the orlando skyline for international conference',
  //       image: icdcImage,
  //       category: 'Branding'
  //     },
  //     {
  //       title: 'Team 44 Character Design',
  //       description: 'Custom illustrated avatars for organizational branding',
  //       image: figuresImage,
  //       category: 'Branding'
  //     },
  //   ],
  //   'Social Media': [
  //     {
  //       title: 'Make a Statement Project',
  //       description: 'Social impact campaign with responsive design across platforms',
  //       image: statementImage,
  //       category: 'Social Media'
  //     },
  //     {
  //       title: 'DYMUN Social Media',
  //       description: 'Comprehensive social media campaign for Model UN',
  //       image: dymunImage,
  //       category: 'Social Media'
  //     },
  //   ],
  //   Educational: [
  //     {
  //       title: 'Sleep Health Series',
  //       description: 'Educational content series about healthy sleep habits',
  //       image: statementImage,
  //       category: 'Educational'
  //     },
  //     {
  //       title: 'Challenge Series',
  //       description: 'Business program educational materials',
  //       image: dymunImage,
  //       category: 'Educational'
  //     },
  //   ],
  // };

  // Lightbox navigation helpers
  const closeLightbox = () => setSelectedGraphicIndex(null);
  const showPrevGraphic = () =>
    setSelectedGraphicIndex(prev =>
      prev === null ? null : (prev - 1 + graphicDesignImages.length) % graphicDesignImages.length
    );
  const showNextGraphic = () =>
    setSelectedGraphicIndex(prev =>
      prev === null ? null : (prev + 1) % graphicDesignImages.length
    );

  // Keyboard navigation for the lightbox
  useEffect(() => {
    if (selectedGraphicIndex === null) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') showPrevGraphic();
      if (e.key === 'ArrowRight') showNextGraphic();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGraphicIndex]);

  // Preload all art and graphic design images on component mount
  useEffect(() => {
    // Preload art images
    Object.values(art).forEach(category => {
      category.forEach(item => {
        preloadImage(item.image);
      });
    });
    
    // Preload graphic design images
    graphicDesignImages.forEach(img => {
      preloadImage(img);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Save to localStorage whenever dark mode changes
    localStorage.setItem('darkMode', isDark.toString());
    
    if (isDark) {
      document.body.style.backgroundColor = '#111827';
      document.documentElement.style.backgroundColor = '#111827';
    } else {
      document.body.style.backgroundColor = '';
      document.documentElement.style.backgroundColor = '';
    }
    
    // Cleanup on unmount
    return () => {
      document.body.style.backgroundColor = '';
      document.documentElement.style.backgroundColor = '';
    };
  }, [isDark]);

  return (
    <div className={`container ${isDark ? 'dark' : ''}`}>

      <header className="header">
        <nav className="nav">
          <Link to="/" className="nav-link">home</Link>
          <Link to="/experience" className="nav-link">experience</Link>
          <Link to="/projects" className="nav-link">projects</Link>
          <a href="/design" className="nav-link">design</a>
          <button 
            onClick={() => setIsDark(!isDark)}
            className="theme-toggle"
            aria-label="Toggle theme"
          >
            {isDark ? <Sun size={20} /> : <Moon size={20} />}
          </button>
        </nav>
      </header>

      {/* <div className="header-spacing">
        <h2 className="page-title">graphic design</h2>
        <p className="highlight">
          created 200+ graphics for ~15 clubs/organizations
        </p>
        
      </div>

      <div className="tabs">
        {Object.keys(projects).map((tab) => (
          <button
            key={tab}
            className={`tab ${activeDesignTab === tab ? 'active' : ''}`}
            onClick={() => setActiveDesignTab(tab)}
          >
            {tab.toLowerCase()}
          </button>
        ))}
      </div>

      <div className="projects-grid">
        {projects[activeDesignTab].map((project, index) => (
          <div key={index} className="project-card">
            <img
              src={project.image}
              alt={project.title.toLowerCase()}
              className="project-image"
            />
            <div className="project-info">
              <span className="project-category">{project.category.toLowerCase()}</span>
              <h3>{project.title.toLowerCase()}</h3>
              <p>{project.description.toLowerCase()}</p>
            </div>
          </div>
        ))}
      </div> */}

      <div className="header-spacing">
        <br></br>
        <h1 className="page-title">ART</h1>
        <p className="page-description">
        my art journey began when i could pick up a pencil, and it’s been my happy place ever since
        </p>
        <p className="page-description">over the years, i’ve created over 1,500 pieces from gouache to charcoal & post on <a href="https://www.instagram.com/torontoartistt/" target="_blank" rel="noopener noreferrer"><span className="highlight">@torontoartistt</span></a></p>
        
        <div className="awards">
          <span className="award-badge">
          ocaa outstanding student art exhibition
          </span>
          <span className="award-badge">
          semifinalist crown education challenge
          </span>
          <span className="award-badge">
          gold key scholastic art & writing
          </span>
        </div>
      </div>

      <div className="tabs">
        {Object.keys(art).map((tab) => (
          <button
            key={tab}
            className={`tab ${activeArtTab === tab ? 'active' : ''}`}
            onClick={() => setActiveArtTab(tab)}
          >
            {tab.toLowerCase()}
          </button>
        ))}
      </div>

      <div className="projects-grid">
        {art[activeArtTab].map((art, index) => (
          <div key={index} className="art-card">
            <img
              src={art.image}
              alt={`${activeArtTab.toLowerCase()} artwork ${index + 1}`}
              className="project-image"
              loading={index < 3 ? "eager" : "lazy"}
              fetchPriority={index < 3 ? "high" : "auto"}
              decoding="async"
            />
          </div>
        ))}
      </div>
      
      <section id="graphic-design-grid" className="graphic-design-section">
        <h2 className="page-title">GRAPHIC DESIGN</h2>
        <p className="page-description">
        created 200+ graphics for ~30 clubs, non-profits, & businesses
        </p>
        <br />
        <div className="graphic-grid">
          {graphicDesignImages.map((image, index) => (
            <button
              key={index}
              className="graphic-item"
              onClick={() => setSelectedGraphicIndex(index)}
              aria-label={`View graphic design ${index + 1}`}
            >
              <img 
                src={image} 
                alt={`Graphic Design ${index + 1}`}
                loading={index < 6 ? "eager" : "lazy"}
                fetchPriority={index < 6 ? "high" : "auto"}
                decoding="async"
              />
            </button>
          ))}
        </div>
      </section>

      {selectedGraphicIndex !== null && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <button className="lightbox-close" onClick={closeLightbox} aria-label="Close">
            ×
          </button>
          <button
            className="lightbox-nav lightbox-prev"
            onClick={(e) => { e.stopPropagation(); showPrevGraphic(); }}
            aria-label="Previous"
          >
            ‹
          </button>
          <img
            className="lightbox-image"
            src={graphicDesignImages[selectedGraphicIndex]}
            alt={`Graphic Design ${selectedGraphicIndex + 1}`}
            onClick={(e) => e.stopPropagation()}
          />
          <button
            className="lightbox-nav lightbox-next"
            onClick={(e) => { e.stopPropagation(); showNextGraphic(); }}
            aria-label="Next"
          >
            ›
          </button>
          <span className="lightbox-counter">
            {selectedGraphicIndex + 1} / {graphicDesignImages.length}
          </span>
        </div>
      )}
      
      <footer className="footer">
      <p>made with <span className="highlight">♥</span> in <span className="highlight">toronto</span></p>
      </footer>
      <br></br>
    </div>
    
    
  );
};

export default Design; 